import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import {
  X, Upload, Link, Check, Folder, FolderOpen, Image, Layers,
} from 'lucide-react';
import { colors, transitions } from '../styles/theme';
import brandAssetActions from '../actions/brandAssets';
import Modal from './Modal';

const UPLOADED_FOLDER_NAME = 'Uploaded';

export default function AssetPickerModal({ brandId, open, onClose, onConfirm }) {
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = All
  const [assets, setAssets] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef(null);
  // Accumulates every asset seen while browsing folders so confirm works across folders
  const seenAssetsRef = useRef({});

  const loadFolders = useCallback(async () => {
    if (!brandId) return;
    setLoadingFolders(true);
    try {
      const res = await brandAssetActions.listFolders(brandId);
      if (res.success) setFolders(res.data || []);
    } catch { /* silent */ } finally { setLoadingFolders(false); }
  }, [brandId]);

  const loadAssets = useCallback(async () => {
    if (!brandId) return;
    setLoadingAssets(true);
    try {
      const params = { limit: 100 };
      if (selectedFolderId) params.folder_id = selectedFolderId;
      const res = await brandAssetActions.listAssets(brandId, params);
      const loaded = res.success ? (res.data?.assets || res.data || []) : [];
      // Accumulate into seen map so multi-folder selections are preserved
      loaded.forEach(a => { seenAssetsRef.current[a.asset_id] = a; });
      if (res.success) setAssets(loaded);
    } catch { /* silent */ } finally { setLoadingAssets(false); }
  }, [brandId, selectedFolderId]);

  useEffect(() => {
    if (!open || !brandId) return;
    loadFolders();
  }, [open, brandId, loadFolders]);

  useEffect(() => {
    if (!open || !brandId) return;
    loadAssets();
  }, [open, brandId, selectedFolderId, loadAssets]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setSelectedAssetIds(new Set());
      setSelectedFolderId(null);
      setUrlInput('');
      setShowUrlInput(false);
      seenAssetsRef.current = {};
    }
  }, [open]);

  const getOrCreateUploadedFolder = async () => {
    // Fetch fresh list to avoid stale state
    const res = await brandAssetActions.listFolders(brandId);
    const fresh = res.success ? (res.data || []) : folders;
    let found = fresh.find(f => f.name.toLowerCase() === UPLOADED_FOLDER_NAME.toLowerCase() && !f.parent_folder_id);
    if (!found) {
      const cr = await brandAssetActions.createFolder(brandId, { name: UPLOADED_FOLDER_NAME });
      if (cr.success) found = cr.data;
    }
    // Refresh local folder list
    setFolders(res.success ? (res.data.some(f => f.folder_id === found?.folder_id) ? res.data : [...res.data, found]) : folders);
    return found?.folder_id || null;
  };

  const handleFileUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const folderId = await getOrCreateUploadedFolder();
      await Promise.all(
        Array.from(files).map(file =>
          brandAssetActions.uploadAndCreateAsset(brandId, file, { folder_id: folderId, asset_type: 'other', usage_type: 'brand_identity' })
        )
      );
      message.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
      setSelectedFolderId(folderId);
      await loadFolders();
      await loadAssets();
    } catch { message.error('Upload failed'); } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUrlAdd = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setUploading(true);
    try {
      const folderId = await getOrCreateUploadedFolder();
      await brandAssetActions.createAssetFromUrl(brandId, url, {
        folder_id: folderId,
        asset_type: 'other',
        usage_type: 'brand_identity',
        name: url.split('/').pop()?.split('?')[0] || 'image',
      });
      message.success('Image added');
      setUrlInput('');
      setShowUrlInput(false);
      setSelectedFolderId(folderId);
      await loadFolders();
      await loadAssets();
    } catch { message.error('Failed to add image'); } finally { setUploading(false); }
  };

  const toggleAsset = (assetId) => {
    setSelectedAssetIds(prev => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
  };

  const handleConfirm = () => {
    // Resolve selected IDs from the accumulated seen-assets map (works across all folders)
    const selected = [...selectedAssetIds]
      .map(id => seenAssetsRef.current[id])
      .filter(Boolean);
    onConfirm(selected);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} width={880} maxWidth="94vw" maxHeight="88vh" aria-label="Select Assets">
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px',
              borderBottom: `1px solid ${colors.border.secondary}`,
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Layers size={15} color={colors.brand.primary} />
                <span style={{ fontSize: 14, fontWeight: 600, color: colors.text.primary }}>
                  Select Assets
                </span>
                {selectedAssetIds.size > 0 && (
                  <span style={{
                    fontSize: 11, fontWeight: 600,
                    color: colors.brand.primary,
                    background: `${colors.brand.primary}18`,
                    border: `1px solid ${colors.brand.primary}30`,
                    borderRadius: 20, padding: '1px 8px',
                  }}>
                    {selectedAssetIds.size} selected
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                aria-label="Close asset picker"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text.disabled, display: 'flex', padding: 4, borderRadius: 6 }}
                onMouseEnter={e => { e.currentTarget.style.color = colors.text.secondary; e.currentTarget.style.background = colors.surface.primary; }}
                onMouseLeave={e => { e.currentTarget.style.color = colors.text.disabled; e.currentTarget.style.background = 'none'; }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Body: two panes */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Left: Folders */}
              <div style={{
                width: 200, flexShrink: 0,
                borderRight: `1px solid ${colors.border.secondary}`,
                overflowY: 'auto',
                padding: '8px 0',
              }}>
                <FolderRow
                  label="All Assets"
                  icon={<Image size={13} />}
                  active={selectedFolderId === null}
                  onClick={() => setSelectedFolderId(null)}
                />
                {loadingFolders ? (
                  <div style={{ padding: '12px 16px' }}>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} style={{ height: 28, borderRadius: 6, background: colors.surface.primary, marginBottom: 4, animation: 'assetPulse 1.5s ease-in-out infinite' }} />
                    ))}
                  </div>
                ) : (
                  folders.map(folder => (
                    <FolderRow
                      key={folder.folder_id}
                      label={folder.name}
                      icon={selectedFolderId === folder.folder_id ? <FolderOpen size={13} /> : <Folder size={13} />}
                      active={selectedFolderId === folder.folder_id}
                      onClick={() => setSelectedFolderId(folder.folder_id)}
                    />
                  ))
                )}
                {!loadingFolders && folders.length === 0 && (
                  <div style={{ padding: '12px 16px', fontSize: 11, color: colors.text.disabled }}>
                    No folders yet
                  </div>
                )}
              </div>

              {/* Right: Asset grid */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {loadingAssets ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                    {[...Array(12)].map((_, i) => (
                      <div key={i} style={{ aspectRatio: '1', borderRadius: 8, background: colors.surface.primary, animation: 'assetPulse 1.5s ease-in-out infinite' }} />
                    ))}
                    <style>{`@keyframes assetPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
                  </div>
                ) : assets.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: colors.text.disabled }}>
                    <Image size={36} color={colors.text.disabled} />
                    <div style={{ fontSize: 13, color: colors.text.tertiary }}>No assets in this folder</div>
                    <div style={{ fontSize: 11, color: colors.text.disabled }}>Upload images below to get started</div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                    {assets.map(asset => {
                      const sel = selectedAssetIds.has(asset.asset_id);
                      return (
                        <div
                          key={asset.asset_id}
                          onClick={() => toggleAsset(asset.asset_id)}
                          title={asset.name || ''}
                          style={{
                            position: 'relative',
                            aspectRatio: '1',
                            borderRadius: 8,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            background: colors.background.tertiary,
                            border: `2px solid ${sel ? colors.brand.primary : 'transparent'}`,
                            outline: sel ? `3px solid ${colors.brand.primary}20` : 'none',
                            transition: 'border-color 0.12s, outline 0.12s',
                          }}
                        >
                          <img
                            src={asset.url}
                            alt={asset.name || ''}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', filter: sel ? 'brightness(0.55)' : 'none', transition: 'filter 0.12s' }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                          <AnimatePresence>
                            {sel && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.7 }}
                                transition={{ duration: 0.1 }}
                                style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
                              >
                                <div style={{ width: 26, height: 26, borderRadius: '50%', background: colors.brand.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                                  <Check size={14} color="#fff" strokeWidth={3} />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                          {/* Name label on hover */}
                          {asset.name && (
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 5px 4px', background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', fontSize: 9, color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {asset.name}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 16px',
              borderTop: `1px solid ${colors.border.secondary}`,
              flexShrink: 0,
              background: colors.background.tertiary,
            }}>
              {/* Upload file */}
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                style={pillBtnStyle(uploading)}
                onMouseEnter={e => { if (!uploading) e.currentTarget.style.borderColor = colors.border.focus; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border.primary; }}
              >
                <Upload size={11} />
                {uploading ? 'Uploading…' : 'Upload Image'}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { handleFileUpload(e.target.files); }} />

              {/* Enter URL */}
              <AnimatePresence mode="wait">
                {showUrlInput ? (
                  <motion.div
                    key="url-input"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}
                  >
                    <input
                      autoFocus
                      value={urlInput}
                      onChange={e => setUrlInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleUrlAdd(); if (e.key === 'Escape') { setShowUrlInput(false); setUrlInput(''); } }}
                      placeholder="https://example.com/image.jpg"
                      style={{ padding: '5px 10px', background: colors.background.secondary, border: `1px solid ${colors.border.primary}`, borderRadius: 6, color: colors.text.primary, fontSize: 12, outline: 'none', width: 240 }}
                    />
                    <button
                      type="button"
                      onClick={handleUrlAdd}
                      disabled={!urlInput.trim() || uploading}
                      style={{ ...pillBtnStyle(!urlInput.trim() || uploading), background: `${colors.brand.primary}22`, borderColor: `${colors.brand.primary}50`, color: colors.brand.primary }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowUrlInput(false); setUrlInput(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text.disabled, display: 'flex', padding: 2 }}
                    >
                      <X size={13} />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="url-btn"
                    type="button"
                    onClick={() => setShowUrlInput(true)}
                    style={pillBtnStyle(false)}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = colors.border.focus; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border.primary; }}
                  >
                    <Link size={11} />
                    Add URL
                  </motion.button>
                )}
              </AnimatePresence>

              <div style={{ flex: 1 }} />

              {/* Selected count + Continue */}
              {selectedAssetIds.size > 0 && (
                <span style={{ fontSize: 12, color: colors.text.tertiary }}>
                  {selectedAssetIds.size} selected
                </span>
              )}
              <button
                type="button"
                onClick={handleConfirm}
                disabled={selectedAssetIds.size === 0}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 18px',
                  background: selectedAssetIds.size > 0 ? 'linear-gradient(135deg, #667eea, #f093fb)' : colors.surface.primary,
                  border: 'none',
                  borderRadius: 20,
                  color: selectedAssetIds.size > 0 ? '#fff' : colors.text.disabled,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: selectedAssetIds.size > 0 ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  boxShadow: selectedAssetIds.size > 0 ? '0 2px 12px rgba(102,126,234,0.4)' : 'none',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => { if (selectedAssetIds.size > 0) e.currentTarget.style.boxShadow = '0 4px 20px rgba(102,126,234,0.6)'; }}
                onMouseLeave={e => { if (selectedAssetIds.size > 0) e.currentTarget.style.boxShadow = '0 2px 12px rgba(102,126,234,0.4)'; }}
              >
                Continue
                {selectedAssetIds.size > 0 && <span style={{ opacity: 0.8 }}>→</span>}
              </button>
            </div>
    </Modal>
  );
}

function FolderRow({ label, icon, active, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 14px',
        cursor: 'pointer',
        background: active ? `${colors.brand.primary}15` : hov ? colors.surface.primary : 'transparent',
        borderLeft: `2px solid ${active ? colors.brand.primary : 'transparent'}`,
        transition: transitions.fast,
        marginBottom: 1,
      }}
    >
      <span style={{ color: active ? colors.brand.primary : colors.text.tertiary, display: 'flex', flexShrink: 0 }}>{icon}</span>
      <span style={{ fontSize: 12, color: active ? colors.text.primary : colors.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </div>
  );
}

function pillBtnStyle(disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '5px 12px',
    background: 'transparent',
    border: `1px solid ${colors.border.primary}`,
    borderRadius: 20,
    color: disabled ? colors.text.disabled : colors.text.secondary,
    fontSize: 12,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'border-color 0.15s',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };
}
